import { useQuery } from '@tanstack/react-query'
import { Input, SpinnerWithBackground, Text, Toggle } from '@viapeople/components'
import { useEffect } from 'react'
import { trpc } from '../../../utils/trpc'
import type { EventMapping } from '../types'

interface TriggerRulesStepProps {
	provider: string
	eventMappings: EventMapping[]
	onChange: (mappings: EventMapping[]) => void
}

export function TriggerRulesStep({ provider, eventMappings, onChange }: TriggerRulesStepProps) {
	const { data: supportedEvents, isLoading } = useQuery({
		...trpc.admin.integrations.crm.getSupportedEvents.queryOptions({ provider }),
		enabled: !!provider,
	})

	// Initialize/merge mappings from supported events when data arrives
	// In edit mode, saved mappings may only contain enabled events — we need to
	// add rows for unsaved events too so they appear as toggle-able options
	useEffect(() => {
		if (supportedEvents) {
			const existingByEvent = new Map(eventMappings.map((m) => [m.crmEvent, m]))

			// Only update if there are missing events (avoids infinite loop)
			const hasMissing = supportedEvents.some((e) => !existingByEvent.has(e.event))
			if (hasMissing || eventMappings.length === 0) {
				const mergedMappings = supportedEvents.map(
					(event) =>
						existingByEvent.get(event.event) || {
							crmEvent: event.event,
							viaPeopleEvent: 'cycle_launch',
							enabled: false,
						},
				)
				onChange(mergedMappings)
			}
		}
	}, [supportedEvents, eventMappings.length, eventMappings.map, onChange]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleToggle = (index: number, enabled: boolean) => {
		const updated = [...eventMappings]
		updated[index] = { ...updated[index], enabled }
		onChange(updated)
	}

	const handleTriggerFieldChange = (index: number, value: string) => {
		const updated = [...eventMappings]
		updated[index] = { ...updated[index], triggerField: value }
		onChange(updated)
	}

	const handleTriggerValuesChange = (index: number, raw: string) => {
		const updated = [...eventMappings]
		// Store the raw comma-separated string verbatim. Splitting/trimming happens
		// once at save time in IntegrationWizard. Splitting eagerly here would strip
		// trailing commas on every keystroke and make multi-value entry impossible.
		updated[index] = { ...updated[index], triggerValuesRaw: raw }
		onChange(updated)
	}

	if (isLoading) {
		return <SpinnerWithBackground backgroundClass='opacity-100' />
	}

	return (
		<div className='flex flex-col gap-6'>
			<div>
				<Text variant='medium' weight='semibold' className='mb-1'>
					Trigger Rules
				</Text>
				<Text variant='small' className='text-neutral-600'>
					Select which CRM events should trigger a viaPeople cycle launch.
				</Text>
			</div>

			<div className='border border-neutral-200 rounded-lg divide-y divide-neutral-200 overflow-hidden'>
				{eventMappings.map((mapping, index) => {
					const eventInfo = supportedEvents?.find((e) => e.event === mapping.crmEvent)
					return (
						<div key={mapping.crmEvent} className='flex flex-col bg-white hover:bg-neutral-50 transition-colors'>
							<div className='flex items-center justify-between p-4'>
								<div className='flex-1 pr-4'>
									<Text variant='small' weight='semibold'>
										{eventInfo?.label || mapping.crmEvent}
									</Text>
									<Text variant='xsmall' className='text-neutral-500 mt-0.5'>
										{eventInfo?.description || `Triggers when ${mapping.crmEvent} occurs`}
									</Text>
								</div>
								<div className='flex items-center gap-3'>
									<Text variant='xsmall' className='text-neutral-400'>
										→ cycle_launch
									</Text>
									<Toggle
										checked={mapping.enabled}
										onChange={(e) => handleToggle(index, e.target.checked)}
										size='small'
										aria-label={`Enable ${mapping.crmEvent}`}
									/>
								</div>
							</div>

							{mapping.enabled && (
								<div className='px-4 pb-4 pt-0 grid grid-cols-2 gap-3 border-t border-dashed border-neutral-200 bg-neutral-50'>
									<Input
										label='Trigger field'
										value={mapping.triggerField ?? ''}
										onChange={(e) => handleTriggerFieldChange(index, e.target.value)}
										placeholder={provider === 'dealcloud' ? 'e.g. Stage, Mandate Status' : 'e.g. StageName, Status__c'}
									/>
									<Input
										label='Match values (comma-separated)'
										value={mapping.triggerValuesRaw ?? (mapping.triggerValues ?? []).join(', ')}
										onChange={(e) => handleTriggerValuesChange(index, e.target.value)}
										placeholder={provider === 'dealcloud' ? 'e.g. Mandate Won, Closed Won' : 'e.g. Closed Won'}
									/>
									<Text variant='xsmall' className='text-neutral-500 col-span-2'>
										Leave both blank to use the legacy defaults baked into the polling service. Set both to override —
										either alone has no effect.
									</Text>
								</div>
							)}
						</div>
					)
				})}

				{eventMappings.length === 0 && !isLoading && (
					<div className='p-8 text-center'>
						<Text variant='small' className='text-neutral-500'>
							No events available for this provider.
						</Text>
					</div>
				)}
			</div>

			<div className='p-3 bg-primary-50 border border-primary-200 rounded-lg'>
				<Text variant='xsmall' className='text-primary-700'>
					<strong>Note:</strong> At least one trigger rule must be enabled for the integration to process incoming
					events.
				</Text>
			</div>
		</div>
	)
}
